import { useConfig } from '../context/ConfigContext';
import {
  getBookingDateGroups,
  buildCourtScheduleLines,
  formatDateTimeSpan,
  formatBookingDate,
} from '../utils/bookingStatusFormatUtil';

/**
 * The "Date & Time" and "Courts & Time" boxes on the Track Booking card.
 *
 * Both list every date of the reservation, so a multi-date booking reads
 * the same way as a single one — one entry per date rather than a separate
 * full-width schedule block. Courts & Time names each date only when there
 * is more than one; with a single date the Date & Time box already says it.
 *
 * Renders two `.info-item` siblings, so it must sit directly inside
 * `.booking-info-grid`.
 *
 * @param {object} props
 * @param {object} props.booking - a grouped booking (see groupBookingRecords)
 * @returns {JSX.Element}
 */
function BookingStatusScheduleBoxes({ booking }) {
  const { config } = useConfig();
  const dateGroups = getBookingDateGroups(booking);
  const isMultiDate = dateGroups.length > 1;

  return (
    <>
      <div className="info-item">
        <label>{isMultiDate ? `Date & Time (${dateGroups.length} dates)` : 'Date & Time'}</label>
        {dateGroups.map((group, idx) => (
          <div className="bss-date-entry" key={group.date || idx}>
            <p className="bss-date">{formatBookingDate(group.date, 'MMMM dd, yyyy')}</p>
            <p className="bss-time">{formatDateTimeSpan(group.timeSlots) || 'N/A'}</p>
          </div>
        ))}
      </div>

      <div className="info-item">
        <label>Courts & Time</label>
        {dateGroups.map((group, idx) => {
          const lines = buildCourtScheduleLines(config, group.timeSlots);
          return (
            <div className="bss-date-entry" key={group.date || idx}>
              {isMultiDate && <p className="bss-court-date">{formatBookingDate(group.date, 'MMM d, yyyy')}</p>}
              {lines.length === 0
                ? <p>N/A</p>
                : lines.map(line => (
                  <p key={line.court}>
                    <span className="bss-court">{line.label}:</span> {line.times}
                  </p>
                ))}
            </div>
          );
        })}
      </div>
    </>
  );
}

export default BookingStatusScheduleBoxes;
